import React, { useEffect, useState } from 'react';
import { getSiteKey, verifyRecaptcha } from '../../Actions/Recaptcha.action';
import { translate } from '../../Services/translation';

const RECAPTCHA_V3_SCRIPT_ID = 'recaptcha-v3-script';
const RECAPTCHA_API_BASE = 'https://www.google.com/recaptcha/api.js';

function loadRecaptchaV3Script(siteKey) {
    return new Promise((resolve, reject) => {
        const existing = document.getElementById(RECAPTCHA_V3_SCRIPT_ID);
        if (existing) {
            if (window.grecaptcha && window.grecaptcha.ready) {
                window.grecaptcha.ready(resolve);
            } else {
                existing.addEventListener('load', () => window.grecaptcha?.ready(resolve));
            }
            return;
        }
        const script = document.createElement('script');
        script.id = RECAPTCHA_V3_SCRIPT_ID;
        script.src = `${RECAPTCHA_API_BASE}?render=${encodeURIComponent(siteKey)}`;
        script.async = true;
        script.onload = () => {
            if (window.grecaptcha && window.grecaptcha.ready) {
                window.grecaptcha.ready(resolve);
            } else {
                resolve();
            }
        };
        script.onerror = () => reject(new Error('reCAPTCHA v3 script failed to load'));
        document.head.appendChild(script);
    });
}

const Recaptcha = (props) => {
    const [showError, setShowError] = useState(false);
    const action = props.action || 'submit';

    useEffect(() => {
        const inputElement = document.getElementById(props.submitId);
        if (inputElement) inputElement.disabled = true;
    }, [props.submitId]);

    useEffect(() => {
        let cancelled = false;

        const init = async () => {
            try {
                const siteKeyRaw = await getSiteKey();
                const siteKey = typeof siteKeyRaw === 'string' && siteKeyRaw.startsWith('"') ? JSON.parse(siteKeyRaw) : siteKeyRaw;
                if (!siteKey || cancelled) return;

                await loadRecaptchaV3Script(siteKey);
                if (cancelled) return;

                const token = await window.grecaptcha.execute(siteKey, { action });
                if (cancelled) return;

                const response = await verifyRecaptcha(token);
                const data = await response.json();
                if (cancelled) return;

                const inputElement = document.getElementById(props.submitId);
                if (data.success && inputElement) {
                    inputElement.disabled = false;
                } else {
                    setShowError(true);
                }
            } catch (e) {
                if (!cancelled) setShowError(true);
            }
        };
        init();
        return () => { cancelled = true; };
    }, [props.submitId, action]);

    return (
        <>
            {showError
                ? <div className="error-message">{translate('recaptcha.error')}</div>
                : <div id="recaptcha-container" className="recaptcha-v3-container" aria-live="polite" />}
        </>
    );
};

export async function executeRecaptcha(action = 'submit') {
    const siteKeyRaw = await getSiteKey();
    const siteKey = typeof siteKeyRaw === 'string' && siteKeyRaw.startsWith('"') ? JSON.parse(siteKeyRaw) : siteKeyRaw;
    if (!siteKey) {
        return null;
    }

    await loadRecaptchaV3Script(siteKey);
    return window.grecaptcha.execute(siteKey, { action });
}

export default Recaptcha;
